import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, Edit2 } from 'lucide-react';
import { canWrite } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPLOYEE_CATEGORIES = [
  'All',
  'Administrative',
  'Woven Line',
  'Knits Line',
  'Cutting Line',
  'Finishing Line'
];

const getCategoryTranslationKey = (category) => {
  const keyMap = {
    'All': 'all',
    'Administrative': 'administrative',
    'Woven Line': 'wovenLine',
    'Knits Line': 'knitsLine',
    'Cutting Line': 'cuttingLine',
    'Finishing Line': 'finishingLine'
  };
  return keyMap[category] || category;
};

export const Employees = ({ forcedCategory = null }) => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(forcedCategory || 'All');
  const [blurSalary, setBlurSalary] = useState(() => {
    const saved = localStorage.getItem('employeesBlurSalary');
    return saved === 'true';
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    employee_number: '',
    name: '',
    category: 'Administrative',
    salary: 0,
    ss: 0,
    vacation_days: 0
  });

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, { headers: getAuthHeader() });
      setEmployees(response.data);
      filterEmployees(response.data, selectedCategory);
    } catch (error) {
      toast.error('Failed to fetch employees');
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const filterEmployees = (empList, category) => {
    if (category === 'All') {
      setFilteredEmployees(empList);
    } else {
      setFilteredEmployees(empList.filter(emp => emp.category === category));
    }
  };

  useEffect(() => {
    filterEmployees(employees, selectedCategory);
  }, [selectedCategory, employees]);

  const handleBlurToggle = (checked) => {
    setBlurSalary(checked);
    localStorage.setItem('employeesBlurSalary', checked.toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        await axios.put(`${API}/employees/${editingEmployee}`, formData, { headers: getAuthHeader() });
        toast.success('Employee updated successfully');
      } else {
        await axios.post(`${API}/employees`, formData, { headers: getAuthHeader() });
        toast.success('Employee added successfully');
      }
      setIsOpen(false);
      setIsEditMode(false);
      setEditingEmployee(null);
      fetchEmployees();
      setFormData({
        employee_number: '',
        name: '',
        category: 'Administrative',
        salary: 0,
        ss: 0,
        vacation_days: 0
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'add'} employee`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (emp) => {
    setIsEditMode(true);
    setEditingEmployee(emp.employee_number);
    setFormData({
      employee_number: emp.employee_number,
      name: emp.name,
      category: emp.category,
      salary: emp.salary,
      ss: emp.ss,
      vacation_days: emp.vacation_days
    });
    setIsOpen(true);
  };

  const handleDelete = async (employee_number) => {
    if (user.role !== 'admin') {
      toast.error('Only admin can delete');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      await axios.delete(`${API}/employees/${employee_number}`, { headers: getAuthHeader() });
      toast.success('Employee deleted');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/employees/upload-deductibles`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to upload CSV');
    }
  };

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('employees')}</h2>
          {forcedCategory ? (
            <span className="text-sm font-medium px-3 py-2">{t(getCategoryTranslationKey(forcedCategory))}</span>
          ) : (
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="category-filter-select"
            >
              {EMPLOYEE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{t(getCategoryTranslationKey(cat))}</option>
              ))}
            </select>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={blurSalary}
              onChange={(e) => handleBlurToggle(e.target.checked)}
              className="w-4 h-4"
              data-testid="blur-salary-toggle-employees"
            />
            <span className="text-sm" lang={language === 'ar' ? 'ar' : 'en'}>{t('blurSalary')}</span>
          </label>
        </div>
        <div className="flex gap-2">
          {user?.role === 'admin' && (
            <>
              <Label htmlFor="csv-upload" className="cursor-pointer">
                <div className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 border border-input" data-testid="upload-csv-btn">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm">{t('uploadCSV')}</span>
                </div>
                <input
                  id="csv-upload"
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="csv-file-input"
                />
              </Label>
            </>
          )}
          {canWrite(user?.role) && <Dialog open={isOpen} onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) {
              setIsEditMode(false);
              setEditingEmployee(null);
              setFormData({
                employee_number: '',
                name: '',
                category: 'Administrative',
                salary: 0,
                ss: 0,
                vacation_days: 0
              });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-accent hover:bg-accent/90" data-testid="add-employee-btn">
                <Plus className="w-4 h-4" />
                <span lang={language === 'ar' ? 'ar' : 'en'}>{t('add')}</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{isEditMode ? t('editEmployee') : t('addEmployee')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('employeeNumber')}</Label>
                  <Input value={formData.employee_number} onChange={(e) => setFormData({...formData, employee_number: e.target.value})} required disabled={isEditMode} data-testid="employee-number-input" />
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('name')}</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="employee-name-input" />
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('category')}</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="employee-category-select"
                  >
                    {EMPLOYEE_CATEGORIES.filter(cat => cat !== 'All').map((cat) => (
                      <option key={cat} value={cat}>{t(getCategoryTranslationKey(cat))}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('salary')}</Label>
                  <Input type="number" step="0.01" value={formData.salary} onChange={(e) => setFormData({...formData, salary: parseFloat(e.target.value)})} required data-testid="employee-salary-input" />
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('socialSecurity')}</Label>
                  <Input type="number" step="0.01" value={formData.ss} onChange={(e) => setFormData({...formData, ss: parseFloat(e.target.value)})} required data-testid="employee-ss-input" />
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('vacationDays')}</Label>
                  <Input type="number" value={formData.vacation_days} onChange={(e) => setFormData({...formData, vacation_days: parseInt(e.target.value)})} data-testid="employee-vacation-input" />
                </div>
                <div className="bg-muted/30 p-3 rounded-md text-sm">
                  <p className="text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>{t('noteOTBonuses')}</p>
                </div>
                <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="employee-submit-btn" disabled={isSubmitting}>
                  <span lang={language === 'ar' ? 'ar' : 'en'}>{t('save')}</span>
                </Button>
              </form>
            </DialogContent>
          </Dialog>}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-muted/50">
            <tr>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('number')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('name')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('category')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('salary')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('ss')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('ot')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('bonuses')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('deductibles')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('withdrawals')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('vacation')}</th>
              {user?.role === 'admin' && <th className="border border-border p-3 text-center font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((emp, idx) => (
              <tr key={idx} className="hover:bg-muted/30" data-testid={`employee-row-${idx}`}>
                <td className="border border-border p-3 font-mono-numeric">{emp.employee_number}</td>
                <td className={`border border-border p-3 ${language === 'ar' ? 'text-base font-bold' : ''}`}>{emp.name}</td>
                <td className="border border-border p-3">{emp.category}</td>
                <td className={`border border-border p-3 text-end font-mono-numeric ${blurSalary ? 'blur-sm select-none' : ''}`}>{emp.salary.toFixed(2)}</td>
                <td className="border border-border p-3 text-end font-mono-numeric">{emp.ss.toFixed(2)}</td>
                <td className="border border-border p-3 text-end font-mono-numeric">{emp.ot.toFixed(2)}</td>
                <td className="border border-border p-3 text-end font-mono-numeric">{emp.bonuses.toFixed(2)}</td>
                <td className="border border-border p-3 text-end font-mono-numeric">{emp.deductibles.toFixed(2)}</td>
                <td className="border border-border p-3 text-end font-mono-numeric">{emp.withdrawals?.toFixed(2) || '0.00'}</td>
                <td className="border border-border p-3 text-end font-mono-numeric">{emp.vacation_days}</td>
                {user?.role === 'admin' && (
                  <td className="border border-border p-3 text-center">
                    <div className="flex gap-1 justify-center">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(emp)} data-testid={`edit-employee-${idx}`}>
                        <Edit2 className="w-4 h-4 text-primary" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(emp.employee_number)} data-testid={`delete-employee-${idx}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {filteredEmployees.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-employees">
            <p lang={language === 'ar' ? 'ar' : 'en'}>{t('noEmployeesInCategory')}</p>
          </div>
        )}
      </div>
    </Card>
  );
};